/**
 * useSlotT -- hook variant of tCms() that reads the current locale from
 * react-i18next automatically. Use in React components:
 *
 *   const slotT = useSlotT('home')
 *   <h2>{slotT('home.about.title')}</h2>
 *
 * Lookup chain:
 *   1. cmsOverrides[locale][key]   CMS override (from generated TS file)
 *   2. i18next.t(key, { lng: locale })   existing JSON fallback
 *   3. '' (empty string)
 *
 * IMPORTANT: `pageSlug` is fixed at hook creation so it can be tree-shaken
 * correctly (the cmsOverrides object is keyed by pageSlug).
 *
 * `locale` may be passed EXPLICITLY. Some pages deliberately pin their
 * content language (the national-education landing renders zh-Hant on /en/),
 * and those pages must resolve their slot values against that same pinned
 * locale — otherwise the CMS override would not match what is on screen.
 */

import { useTranslation } from 'react-i18next'
import type { Locale } from '@/admin/api'
import { tCms as tCmsImpl, tCmsObject as tCmsObjectImpl } from './tCms'
import { useCmsOverrides } from '@/hooks/useCmsOverrides'

export function useSlotT(pageSlug: string, explicitLocale?: Locale): (slotKey: string) => string {
  const { i18n } = useTranslation()
  const ambient = (i18n.language || 'zh-Hant') as Locale
  const locale = explicitLocale ?? ambient
  const overrides = useCmsOverrides()
  return (slotKey: string) => tCmsImpl(pageSlug, slotKey, locale, overrides)
}

/**
 * Resolve a structured (object / array) slot. The i18n default defines the
 * shape; the CMS override supplies a delta that is merged over it.
 */
export function useSlotObject(
  pageSlug: string,
  explicitLocale?: Locale,
): (slotKey: string) => unknown {
  const { i18n } = useTranslation()
  const ambient = (i18n.language || 'zh-Hant') as Locale
  const locale = explicitLocale ?? ambient
  const overrides = useCmsOverrides()
  return (slotKey: string) => tCmsObjectImpl(pageSlug, slotKey, locale, overrides)
}
